import React from 'react';

import { useHighlightText } from './useHighlightText';
import { HighlightMark, NonHighlightMark } from './HBHighlightText.styles';
import type { HBHighlightTextProps } from './HBHighlightText.types';
import { semanticColors } from '../../styles/semanticColors';

/**
 * HBHighlightText component highlights text that matches a search query.
 * Uses background color only (no font-weight changes) to prevent layout shifts.
 * By default, only the first match is highlighted. Set allMatches to true to highlight all matches.
 *
 * @example
 * ```tsx
 * // Highlights only the first occurrence of "manage"
 * <HBHighlightText
 *   query="manage"
 * >
 *   Project Management Dashboard - Manage your projects
 * </HBHighlightText>
 * ```
 *
 * @example
 * ```tsx
 * // Highlights all occurrences
 * <HBHighlightText query="manage" allMatches>
 *   Project Management Dashboard - Manage your projects
 * </HBHighlightText>
 * ```
 *
 * @example
 * ```tsx
 * // Multiple queries
 * <HBHighlightText query={["project", "manage"]}>
 *   Project Management Dashboard
 * </HBHighlightText>
 * ```
 */
export const HBHighlightText: React.FC<HBHighlightTextProps> = ({
    children,
    query,
    highlightBGColor = semanticColors.surface.accent,
    highlightTextColor = semanticColors.text.accent,
    caseSensitive = false,
    allMatches = false,
    'data-testid': dataTestId
}) => {
    const isValidText =
        typeof children === 'string' || typeof children === 'number';

    const segments = useHighlightText({
        text: isValidText ? String(children) : '',
        query,
        caseSensitive,
        allMatches
    });

    if (!isValidText) {
        if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.warn(
                'HBHighlightText: children must be a string or number. Received: ' +
                    typeof children
            );
        }
        return <>{children}</>;
    }

    const hasHighlights = segments.some(seg => seg.isHighlighted);
    if (!hasHighlights) {
        return <>{children}</>;
    }

    return (
        <>
            {segments.map(segment =>
                segment.isHighlighted ? (
                    <HighlightMark
                        key={segment.key}
                        $highlightColor={highlightBGColor}
                        $textColor={highlightTextColor}
                        data-testid={
                            dataTestId ? `${dataTestId}-highlight` : undefined
                        }
                    >
                        {segment.text}
                    </HighlightMark>
                ) : (
                    <NonHighlightMark key={segment.key}>
                        {segment.text}
                    </NonHighlightMark>
                )
            )}
        </>
    );
};

HBHighlightText.displayName = 'HBHighlightText';
